import React, { JSX, useState } from "react";
import {
    IconButton,
    Menu,
    MenuItem,
} from "@mui/material";
import {
    Folder as FolderIcon,
    InsertDriveFile,
    MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { Document, FolderWithDocuments } from "../../types/folderTypes";
import { useNavigate } from "react-router-dom";
import FileViewerModal from "./FileViewerModal";


interface FolderDocumentGridProps {
    folders: FolderWithDocuments[];
    unassignedDocuments: Document[];
    folderAnchorEl: HTMLElement | null;
    fileAnchorEl: HTMLElement | null;
    selectedFolder: FolderWithDocuments | null;
    selectedFile: Document | null;
    userRole: string;
    isCompanyAdmin: number;
    handleFolderClick?: (folderId: string) => void;
    handleFolderMenuOpen?: (event: React.MouseEvent<HTMLButtonElement>, folder: FolderWithDocuments) => void;
    handleFolderMenuClose?: () => void;
    handleEditFolder?: (folder: FolderWithDocuments) => void;
    handleDownloadFolder?: (folder: FolderWithDocuments) => void;
    handleDeleteFolder?: (folderId: number) => void;
    handleDocumentClick?: (documentPath: string) => void;
    handleFileMenuOpen?: (event: React.MouseEvent<HTMLButtonElement>, doc: Document) => void;
    handleFileMenuClose?: () => void;
    handleEditDocument?: (doc: Document) => void;
    handleDownloadFile?: (docId: string, docName: string) => void;
    confirmDeleteFile?: (docId: string) => void;
}

const FolderDocumentGrid: React.FC<FolderDocumentGridProps> = ({
    folders,
    unassignedDocuments,
    folderAnchorEl,
    fileAnchorEl,
    selectedFolder,
    selectedFile,
    userRole,
    isCompanyAdmin,
    handleFolderClick,
    handleFolderMenuOpen,
    handleFolderMenuClose,
    handleEditFolder,
    handleDownloadFolder,
    handleDeleteFolder,
    handleDocumentClick,
    handleFileMenuOpen,
    handleFileMenuClose,
    handleEditDocument,
    handleDownloadFile,
    confirmDeleteFile,
}) => {

    const getFileIcon = (filename: string, documentPath: string): JSX.Element => {
        const ext = filename.split('.').pop()?.toLowerCase();

        // Check if it's an image type
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        if (ext && imageExtensions.includes(ext)) {
            return (
                <img
                    src={documentPath}
                    alt={filename}
                    style={{
                        width: 48,
                        height: 48,
                        objectFit: 'cover',
                        borderRadius: 4,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    }}
                />
            );
        }

        // Otherwise, fallback to icons
        let iconName = 'generic.png';

        switch (ext) {
            case 'doc':
            case 'docx':
                iconName = 'word.png';
                break;
            case 'xls':
            case 'xlsx':
                iconName = 'excel.png';
                break;
            case 'ppt':
            case 'pptx':
                iconName = 'powerpoint.png';
                break;
            case 'pdf':
                iconName = 'pdf.png';
                break;
            case 'txt':
                iconName = 'txt.png';
                break;
            case 'zip':
            case 'rar':
                iconName = 'zip.png';
                break;
        }

        return <img src={`/icon/${iconName}`} alt={ext} style={{ width: 48, height: 48 }} />;
    };

    const navigate = useNavigate();

    const [viewerDoc, setViewerDoc] = useState<Document | null>(null);


    return (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-6">
            {/* Folders */}
            {folders.map((folder) => (
                <div
                    key={folder.id}
                    className="flex flex-col items-center text-center"
                    onClick={() => handleFolderClick?.(folder.id.toString())}
                    onContextMenu={(e: any) => {
                        if (userRole === "user" && String(isCompanyAdmin) === "2") return;
                        e.preventDefault();
                        handleFolderMenuOpen?.(e, folder);
                    }}
                >
                    <FolderIcon className="text-[#632DE9]" style={{ fontSize: 48 }} />
                    <p className="text-sm truncate w-full mt-1">{folder.folder_name}</p>
                </div>
            ))}

            {/* Files */}
            {unassignedDocuments.map((doc) => (
                <div
                    key={doc.id}
                    className="flex flex-col items-center text-center"
                    // onClick={() => handleDocumentClick?.(doc.document_path)}
                    // onClick={() => {
                    //     const encodedUrl = encodeURIComponent(doc.document_path);
                    //     const viewerUrl = `/file-viewer/${encodedUrl}/${encodeURIComponent(doc.document_name)}`;
                    //     window.open(viewerUrl, '_blank', 'noopener,noreferrer');
                    // }}
                    onClick={() => setViewerDoc(doc)}
                    onContextMenu={(e: any) => {
                        if (userRole === "user" || String(isCompanyAdmin) === "2") return;
                        e.preventDefault();
                        handleFileMenuOpen?.(e, doc);
                    }}
                >
                    {getFileIcon(doc.document_name, doc.document_path)}
                    <p className="text-sm truncate w-full mt-1">{doc.document_name}</p>
                </div>
            ))}

            {/* Folder Context Menu */}
            <Menu
                anchorEl={folderAnchorEl}
                open={Boolean(folderAnchorEl) && !!selectedFolder}
                onClose={handleFolderMenuClose}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem onClick={() => { handleEditFolder?.(selectedFolder!); handleFolderMenuClose?.(); }}>
                    Edit
                </MenuItem>
                <MenuItem onClick={() => { handleDeleteFolder?.(selectedFolder!.id); handleFolderMenuClose?.(); }} className="text-red-500">
                    Delete
                </MenuItem>
                <MenuItem onClick={() => { handleDownloadFolder?.(selectedFolder!); handleFolderMenuClose?.(); }}>
                    Download
                </MenuItem>

            </Menu>

            <FileViewerModal document={viewerDoc} onClose={() => setViewerDoc(null)} />

            {/* File Context Menu */}
            <Menu
                anchorEl={fileAnchorEl}
                open={Boolean(fileAnchorEl) && !!selectedFile}
                onClose={handleFileMenuClose}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >

                <MenuItem onClick={() => { handleEditDocument?.(selectedFile!); handleFileMenuClose?.(); }}>
                    Edit
                </MenuItem>

                <MenuItem onClick={() => { handleDownloadFile?.(selectedFile!.id.toString(), selectedFile!.document_name); handleFileMenuClose?.(); }}>
                    Download
                </MenuItem>
                <MenuItem onClick={() => { confirmDeleteFile?.(selectedFile!.id.toString()); handleFileMenuClose?.(); }} className="text-red-500">
                    Delete
                </MenuItem>

            </Menu>
        </div>

    );
};

export default FolderDocumentGrid;
